import type { GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/primitives/button'
import { Input } from '@neo/primitives/input'
import { Label } from '@neo/primitives/label'
import { Textarea } from '@neo/primitives/textarea'
import { Typography } from '@neo/primitives/typography'
import { CheckCircle2 } from 'lucide-react'
import { useState } from 'react'
import {
  AddIcon,
  addButton,
  DeleteButton,
  ExpandableBody,
  FieldBlock,
  RowHead,
  rowShell,
  VariableNameError,
} from './agent-task-row-shell'
import { AgentTaskVariableRow } from './agent-task-variable-row'
import type { AgentTasksValue, ExtractOnlyVariable } from './agent-tasks-config'

// The agent_node variables sections (SER-5959): "Variables to collect" (ordered
// call steps as collapsible cards) and "Captured from conversation" (name +
// instructions only). A plain variable is just asked; attaching an initial
// value makes silent use the default, with "Confirm it first" as an opt-in
// checkbox. Collapsed rows show only the name (plus a required mark). Names
// render exactly as the author wrote them. Copy law: hints describe what the
// agent does on the call; no implementation vocabulary, no agent names.

const ConfirmKnownValuesBlock = ({
  names,
  announcement,
  onAnnouncementChange,
}: {
  names: string[]
  announcement: string
  onAnnouncementChange: (value: string) => void
}) => {
  const [open, setOpen] = useState(false)
  return (
    <div className={`rounded-md bg-bg2 border border-st1 ${open ? '' : 'border-dashed'}`}>
      <RowHead open={open} onToggle={() => setOpen(!open)}>
        <CheckCircle2 size={14} className="shrink-0 text-secondary" />
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <span className="text-s-600">Confirm known values</span>
          <span className="text-xs-400 text-secondary truncate">
            Confirms <span className="text-foreground">{names.join(', ')}</span> at the start of the
            call
          </span>
        </div>
      </RowHead>
      <ExpandableBody open={open}>
        <FieldBlock label="Opening sentence">
          <Textarea
            value={announcement}
            onChange={(e) => onAnnouncementChange(e.target.value)}
            placeholder="I have {details} on file for you."
            className="bg-background min-h-[56px]"
          />
          <Typography variant="XS/400" className="text-secondary mt-1">
            Spoken at the start of the call. The values are read out at {'{details}'}.
          </Typography>
        </FieldBlock>
        <Typography
          variant="XS/400"
          className="text-secondary border-t border-dashed border-st2 pt-2.5"
        >
          This step appears when a variable has &ldquo;Confirm it first&rdquo; enabled.
        </Typography>
      </ExpandableBody>
    </div>
  )
}

const CapturedVariableRow = ({
  row,
  open,
  onToggle,
  onChange,
  onDelete,
}: {
  row: ExtractOnlyVariable
  open: boolean
  onToggle: () => void
  onChange: (patch: Partial<ExtractOnlyVariable>) => void
  onDelete: () => void
}) => (
  <div className={`${rowShell} ${open ? 'border-st1' : ''}`}>
    <RowHead open={open} onToggle={onToggle}>
      <span className="flex-1 min-w-0 text-s-600 truncate">{row.name}</span>
      <DeleteButton onClick={onDelete} />
    </RowHead>
    <ExpandableBody open={open}>
      <FieldBlock label="Variable name">
        <Input
          value={row.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Enter variable name"
        />
        <VariableNameError name={row.name} />
      </FieldBlock>
      <FieldBlock label="Instructions">
        <Textarea
          value={row.instructions}
          onChange={(e) => onChange({ instructions: e.target.value })}
          placeholder="What to listen for, in plain words"
          className="bg-background min-h-[56px]"
        />
      </FieldBlock>
    </ExpandableBody>
  </div>
)

// Module scope so the default is referentially stable — an inline [] default
// re-renders memoized children on every parent render.
const NO_NODE_VARS: string[] = []
// Module scope: an inline {} fallback returns a new object each render, which
// breaks form.useStore referential stability for every agent_tasks consumer.
const EMPTY_AGENT_TASKS: AgentTasksValue = { variables: [], extract_only: [] }

export const AgentTasksSection = ({
  form,
  nodeVars = NO_NODE_VARS,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: host panel owns the form values type; only the agent_tasks subtree is touched here
  form: GenericFormType<any>
  nodeVars?: string[]
}) => {
  const agentTasks: AgentTasksValue = form.useStore(
    (state: { values: { agent_tasks?: AgentTasksValue } }) =>
      state.values.agent_tasks ?? EMPTY_AGENT_TASKS,
  )
  const [openVars, setOpenVars] = useState<Record<string, boolean>>({})
  const [openCaptured, setOpenCaptured] = useState<Record<string, boolean>>({})
  const [dragIndex, setDragIndex] = useState<number | null>(null)

  const variables = agentTasks.variables ?? []
  const captured = agentTasks.extract_only ?? []
  const update = (next: Partial<AgentTasksValue>) => {
    form.setFieldValue('agent_tasks' as never, { ...agentTasks, ...next } as never)
  }
  const updateRow = (index: number, patch: Partial<AgentTasksValue['variables'][number]>) => {
    update({ variables: variables.map((row, i) => (i === index ? { ...row, ...patch } : row)) })
  }
  const updateCaptured = (
    index: number,
    patch: Partial<AgentTasksValue['extract_only'][number]>,
  ) => {
    update({ extract_only: captured.map((row, i) => (i === index ? { ...row, ...patch } : row)) })
  }

  const confirmNames = variables.reduce<string[]>((acc, row) => {
    if (row.known_value !== undefined && row.confirm_if_known) acc.push(row.name)
    return acc
  }, [])

  return (
    <div className="col-span-2 flex flex-col gap-3">
      <div className="mb-1">
        <Label className="text-p-500">Variables to collect</Label>
        <Typography variant="XS/400" className="text-secondary">
          Collected on the call in the order below. Drag to reorder.
        </Typography>
      </div>

      {confirmNames.length > 0 && (
        <ConfirmKnownValuesBlock
          names={confirmNames}
          announcement={agentTasks.confirm_announcement ?? ''}
          onAnnouncementChange={(value) => update({ confirm_announcement: value })}
        />
      )}

      {variables.map((row, index) => (
        <AgentTaskVariableRow
          key={row._uid}
          row={row}
          index={index}
          open={!!openVars[row._uid]}
          onToggle={() => setOpenVars({ ...openVars, [row._uid]: !openVars[row._uid] })}
          dragging={dragIndex === index}
          onDragStart={() => setDragIndex(index)}
          onDrop={() => {
            if (dragIndex === null || dragIndex === index) return
            const next = [...variables]
            const [moved] = next.splice(dragIndex, 1)
            next.splice(index, 0, moved)
            setDragIndex(null)
            update({ variables: next })
          }}
          nodeVars={nodeVars}
          onChange={(patch) => updateRow(index, patch)}
          onDelete={() => update({ variables: variables.filter((_, i) => i !== index) })}
        />
      ))}

      {variables.length === 0 && (
        <Typography variant="XS/400" className="text-secondary">
          No variables yet. The agent will just follow the instructions above.
        </Typography>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        data-testid="button-collect-variable"
        className={addButton}
        onClick={() => {
          const _uid = crypto.randomUUID()
          setOpenVars({ ...openVars, [_uid]: true })
          update({
            variables: [
              ...variables,
              { _uid, name: 'new_variable', instructions: '', type: 'text' },
            ],
          })
        }}
      >
        <AddIcon /> Collect a variable
      </Button>

      <div className="mt-5 mb-1">
        <Label className="text-p-500">Captured from conversation</Label>
        <Typography variant="XS/400" className="text-secondary">
          The agent picks these up from the conversation; the caller is never asked.
        </Typography>
      </div>

      {captured.map((row, index) => (
        <CapturedVariableRow
          key={row._uid}
          row={row}
          open={!!openCaptured[row._uid]}
          onToggle={() => setOpenCaptured({ ...openCaptured, [row._uid]: !openCaptured[row._uid] })}
          onChange={(patch) => updateCaptured(index, patch)}
          onDelete={() => update({ extract_only: captured.filter((_, i) => i !== index) })}
        />
      ))}

      {captured.length === 0 && (
        <Typography variant="XS/400" className="text-secondary">
          Nothing captured silently on this node.
        </Typography>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        data-testid="button-capture-variable"
        className={addButton}
        onClick={() => {
          const _uid = crypto.randomUUID()
          setOpenCaptured({ ...openCaptured, [_uid]: true })
          update({ extract_only: [...captured, { _uid, name: 'new_variable', instructions: '' }] })
        }}
      >
        <AddIcon /> Capture a variable
      </Button>
    </div>
  )
}
